import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang, useL, pick } from "@/lib/i18n";
import { api, errDetail, fmtDate, fmtDateTime } from "@/lib/api";
import { StatusBadge } from "@/components/member/Lists";
import { F, RedBtn, GhostBtn } from "./ui";
import { HandoverButton, KcBadge } from "./Keycard";

const STATUSES = ["none", "active", "past_due", "paused", "canceled"];
const KC = ["unassigned", "issued", "active", "lost", "replaced", "deactivated", "suspended", "returned"];

function Sel({ value, onChange, items, testId }) {
  const { t } = useLang();
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="border-white/15 bg-black/40" data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l ?? t(`status.${v}`)}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function MemberDialog({ id, plans, onClose }) {
  const { lang, tErr } = useLang();
  const L = useL();
  const [d, setD] = useState(null);
  const [hrs, setHrs] = useState({ delta: "", reason: "" });
  const [kc, setKc] = useState({ number: "", status: "unassigned", notes: "" });
  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/members/${id}`);
    setD(data);
    setKc({ number: data.user.keycard?.number || "", status: data.user.keycard?.status || "unassigned", notes: data.user.keycard?.notes || "" });
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, msg = L("Enregistré", "Saved")) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(tErr(errDetail(e))); } };
  const update = (patch) => act(() => api.put(`/admin/members/${id}`, patch));
  if (!d) return null;
  const u = d.user;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0f1015]" data-testid="member-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display text-3xl uppercase">{u.name}</DialogTitle></DialogHeader>
        <p className="-mt-2 text-sm text-slate-400">{u.email} · {u.phone || "—"}</p>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-500/30 p-4" data-testid="member-dialog-handover">
          <p className="text-sm text-slate-300">{L("Remise de carte", "Keycard handover")}: <KcBadge h={u.keycard_handover} /></p>
          <HandoverButton uid={id} h={u.keycard_handover} onDone={load} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><p className="mb-1 text-xs text-slate-400">{L("Statut d'abonnement", "Membership status")}</p>
            <Sel value={u.membership_status} onChange={(v) => update({ membership_status: v })} items={STATUSES.map((s) => [s])} testId="member-status-select" /></div>
          <div><p className="mb-1 text-xs text-slate-400">{L("Forfait", "Plan")}</p>
            <Sel value={u.plan_id} onChange={(v) => update({ plan_id: v })} items={plans.map((p) => [p.id, pick(p, "name", lang)])} testId="member-plan-select" /></div>
        </div>
        <div className="rounded-xl border border-white/10 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-300">{L("Solde", "Balance")}: <span className="font-display text-3xl text-white" data-testid="member-dialog-hours">{Number(u.hours_balance || 0).toFixed(1)} h</span></p>
            <GhostBtn onClick={() => act(() => api.post(`/admin/members/${id}/start-period`), L("Nouvelle période démarrée", "New period started"))} data-testid="member-start-period-btn">{L("Démarrer une période (heures du forfait)", "Start period (plan hours)")}</GhostBtn>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-[120px_1fr_auto] sm:items-end">
            <F id="hours-delta-input" label={L("+/- heures", "+/- hours")} type="number" step="0.5" value={hrs.delta} onChange={(e) => setHrs({ ...hrs, delta: e.target.value })} />
            <F id="hours-reason-input" label={L("Raison", "Reason")} value={hrs.reason} onChange={(e) => setHrs({ ...hrs, reason: e.target.value })} />
            <RedBtn disabled={!hrs.delta || !hrs.reason} data-testid="hours-adjust-btn"
              onClick={() => act(async () => { await api.post(`/admin/members/${id}/adjust-hours`, { delta: Number(hrs.delta), reason: hrs.reason }); setHrs({ delta: "", reason: "" }); })}>{L("Ajuster", "Adjust")}</RedBtn>
          </div>
        </div>
        <div className="rounded-xl border border-white/10 p-4">
          <p className="mb-1 text-sm text-white">{L("Carte d'accès", "Keycard")} <span className="ml-2 rounded-full border border-amber-500/40 px-2 py-0.5 font-mono text-[10px] uppercase text-amber-300">{L("Manuel", "Manual")}</span></p>
          <p className="mb-3 text-xs text-slate-500">{L("Aucun matériel connecté : ces informations servent au suivi seulement.", "No hardware connected: this is for tracking only.")}</p>
          <div className="grid gap-2 sm:grid-cols-3">
            <F id="keycard-number-input" label={L("Numéro", "Number")} value={kc.number} onChange={(e) => setKc({ ...kc, number: e.target.value })} />
            <div><p className="mb-1.5 text-xs text-slate-400">{L("Statut", "Status")}</p><Sel value={kc.status} onChange={(v) => setKc({ ...kc, status: v })} items={KC.map((s) => [s])} testId="keycard-status-select" /></div>
            <F id="keycard-notes-input" label="Notes" value={kc.notes} onChange={(e) => setKc({ ...kc, notes: e.target.value })} />
          </div>
          <RedBtn className="mt-3" data-testid="keycard-save-btn" onClick={() => act(() => api.put(`/admin/members/${id}/keycard`, kc))}>{L("Enregistrer la carte", "Save keycard")}</RedBtn>
        </div>
        <div>
          <p className="mb-2 text-sm text-white">{L("Réservations", "Bookings")}</p>
          <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
            {d.bookings.map((b) => <li key={b.id} className="flex justify-between border-b border-white/5 py-1.5 text-slate-300"><span className="capitalize">{fmtDate(b.local_date, lang)} · {b.local_start}–{b.local_end}</span><StatusBadge s={b.status} /></li>)}
            {!d.bookings.length && <li className="text-slate-500">—</li>}
          </ul>
        </div>
        <div>
          <p className="mb-2 text-sm text-white">{L("Historique des heures", "Hours ledger")}</p>
          <ul className="max-h-40 space-y-1 overflow-y-auto font-mono text-xs" data-testid="member-ledger">
            {d.ledger.map((l) => <li key={l.id} className="flex justify-between text-slate-400"><span>{fmtDateTime(l.created_at, lang)} · {l.reason}</span><span className={l.delta >= 0 ? "text-emerald-300" : "text-red-300"}>{l.delta > 0 ? "+" : ""}{Number(l.delta).toFixed(1)}</span></li>)}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
